import { doc, getDoc, updateDoc, getDocs, query, where, orderBy, limit, startAfter } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { deleteRecordAndFiles, updateFieldsInRecord, updateRecord, userCollectionsDB, uploadFileToGCloud } from "../../libs/firebase";
import { generateID, getActiveDesignFirmId } from "ui";
import { CollectionInterface, CollectionThumbnail, CollectionUserImages, CollectionUserMaterials, validateCollection } from "ts-interfaces";
import { firebaseHelper } from "ui";
import { ProjectSchemeInterface, Permissions } from "ts-interfaces";
import { hasAccessToResource } from "ui/utils/usePermissions";
import { produce } from 'immer'
import { inChunkSize, teamScopeConstraints, type TeamScope } from "./accessQueries";
import { freshAccessFields, stripAccessFields } from "./accessFields";
import { sharingV2Doc } from "ui/utils/workspace/sharingV2";
import { setItemProject } from "ui/utils/workspace/accessApi";

// 🔧 SIMPLIFIED COLLECTION FETCHING FUNCTIONS

/**
 * Fetch collections the user owns directly (no permissions needed)
 */
export async function getOwnedCollections(
  userId: string, 
  currentCollections: CollectionInterface[] = [], 
  limitRecords: number = 20
): Promise<CollectionInterface[]> {
  if (!userCollectionsDB || !userId) return [];
  
  try {
    let q;
    
    if (currentCollections.length > 0) {
      q = query(
        userCollectionsDB,
        where("uid", "==", userId),
        orderBy("updatedAt", "desc"),
        startAfter(currentCollections[currentCollections.length - 1].updatedAt),
        limit(limitRecords)
      );
    } else {
      q = query(
        userCollectionsDB,
        where("uid", "==", userId),
        orderBy("updatedAt", "desc"),
        limit(limitRecords)
      );
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as CollectionInterface);
  } catch (error) {
    console.error('Error fetching owned collections:', error);
    return [];
  }
}

/**
 * Fetch every collection stamped with a team's firm id — all members' team
 * collections, regardless of owner (uid). Used for the team-workspace list
 * view; mirrors getOwnedCollections' cursor pagination on updatedAt.
 */
export async function getTeamCollections(
  designFirmId: string,
  currentCollections: CollectionInterface[] = [],
  limitRecords: number = 20,
  teamScope: TeamScope | null = null
): Promise<CollectionInterface[]> {
  if (!userCollectionsDB || !designFirmId) return [];

  try {
    // Only the collections the user can read; a team list always has a scope.
    if (!teamScope) return [];
    const base = [...teamScopeConstraints(teamScope), orderBy("updatedAt", "desc")];
    const q =
      currentCollections.length > 0
        ? query(
            userCollectionsDB,
            ...base,
            startAfter(currentCollections[currentCollections.length - 1].updatedAt),
            limit(limitRecords)
          )
        : query(userCollectionsDB, ...base, limit(limitRecords));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as CollectionInterface);
  } catch (error) {
    console.error('Error fetching team collections:', error);
    return [];
  }
}

export async function duplicateCollectionScheme(collection: CollectionInterface, permissions: Permissions | null): Promise<CollectionInterface> {
    const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("User not authenticated");

      const hasWritePermission = hasAccessToResource(permissions, collection.id, 'write', 'collection', collection.uid || '', collection.uid || '');
    if (!hasWritePermission) {
      throw new Error("You do not have permission to duplicate this collection")
    }
    
      const duplicatedCollection = {
        ...collection,
        name: `Copy of ${collection.name}`,
        id: generateID(),
        createdAt: Date.now(),
        updatedAt: Date.now(),
        // Reset these fields for the duplicate
        associatedBoardUrl: "",
        associatedProjectId: "",
        writeAccess: [], // Empty array instead of inheriting from original
        // Sharing state never rides along on a copy — the duplicate is private
        // until its new owner publishes it (same rule as ffeSchedules).
        is_public: false,
        uid: user.uid, // Set to the current user creating the duplicate
        // Stamp the copy with the user's current active workspace, not the
        // source collection's (Phase A.3.4).
        design_firm_id: getActiveDesignFirmId(user.uid),
        ...freshAccessFields(user.uid),
      };
    
      try {
        await updateRecord(duplicatedCollection.id, "usersMyCollection", duplicatedCollection);
      } catch (error) {
        console.error("Error updating project:", error); // Log the error
        throw new Error("Failed to update project"); // Optionally rethrow the error with a custom message
      }
      return duplicatedCollection;
}

export async function deleteCollection(collection: CollectionInterface, permissions: Permissions | null) {
  try {
    if (!collection?.id) {
      throw new Error("Collection ID is required for deletion")
    }
  
    // A hard delete is the owner's alone.
    if (!collection.uid || collection.uid !== getAuth().currentUser?.uid) {
      throw new Error("Only the collection's owner can delete it")
    }

    await deleteRecordAndFiles(collection.id, "usersMyCollection")
    return collection
  } catch (error) {
    console.error("Error deleting collection:", error)
    throw new Error(error.message || "Failed to delete collection from database")
  }
}

export async function associateCollectionWithProject(
  collectionId: string, 
  projectId: string | null, 
  user_id: string, 
  permissions: Permissions | null
): Promise<void> {
  const docSnap = await getCollectionIfAccessible(collectionId, user_id, permissions, 'write');
  if (!docSnap) {
    throw new Error("No access to the collection");
  }

  // Filing is pointer-only: since the cutover a project's people reach a
  // team collection through its readers/editors, and writeAccess grants nothing.
  if (projectId) {
    const projectDoc = await getDoc(doc(firebaseHelper.db, "projectsScheme", projectId));
    if (!projectDoc.exists()) {
      throw new Error("Project not found");
    }
    const projectData = projectDoc.data() as ProjectSchemeInterface;
    const hasProjectAccess = hasAccessToResource(permissions, projectId, 'write', 'project', projectData.owner || '', user_id, projectData.design_firm_id ?? null, projectData);
    if (!hasProjectAccess) {
      throw new Error("No access to the project");
    }
  }

  // Update the collection
  await updateDoc(docSnap.ref, {
    associatedProjectId: projectId,
    updatedAt: Date.now()
  });
}

// Bulk association function for multiple collections
export async function associateMultipleCollectionsWithProject(
  collectionsToAdd: string[], 
  collectionsToRemove: string[], 
  projectId: string | null, 
  user_id: string, 
  permissions: Permissions | null
): Promise<void> {
  // Validate project access first if projectId is provided
  if (projectId) {
    const projectDoc = await getDoc(doc(firebaseHelper.db, "projectsScheme", projectId));
    if (!projectDoc.exists()) {
      throw new Error("Project not found");
    }
    
    const projectData = projectDoc.data() as ProjectSchemeInterface;
    const hasProjectAccess = hasAccessToResource(permissions, projectId, 'write', 'project', projectData.owner || '', user_id, projectData.design_firm_id ?? null, projectData);

    if (!hasProjectAccess) {
      throw new Error("No access to the project");
    }
  }

  // Process all collections in parallel
  const allCollectionIds = [...collectionsToAdd, ...collectionsToRemove];
  const collectionPromises = allCollectionIds.map(async (collectionId) => {
    try {
      if (collectionsToAdd.includes(collectionId)) {
        // Associate with project
        await associateCollectionWithProject(collectionId, projectId, user_id, permissions);
      } else {
        // Remove from project (set to null)
        await associateCollectionWithProject(collectionId, null, user_id, permissions);
      }
    } catch (error) {
      console.error(`Error updating collection ${collectionId}:`, error);
      throw error;
    }
  });

  await Promise.all(collectionPromises);
}


// Helper function to get collection if user has access to it
async function getCollectionIfAccessible(collectionId: string, user_id: string, permissions: Permissions | null, accessType: 'read' | 'write' = 'read') {
  if (!user_id) throw new Error("User not authenticated");
  if (!collectionId) throw new Error("Invalid collection ID");

  //we need to assume that a usersMyCollection could be protected by permissions.  May throw an error
  try {
    const docSnap = await getDoc(doc(firebaseHelper.db, "usersMyCollection", collectionId));
    if (!docSnap.exists()) return null;
    const collectionData = docSnap.data() as CollectionInterface;
    const hasSharedAccess = hasAccessToResource(permissions, collectionId, accessType, 'collection', collectionData.uid || '', user_id, null, collectionData);
    if (!hasSharedAccess) {
      throw new Error("You don't have permission to access this collection");
    }
    return docSnap;
  }
  catch (error) {
     throw new Error("You don't have permission to access this collection");
  }
  // const isOwner = collectionData.uid === user_id || collectionData.owner === user_id;
  // const hasSharedAccess = permissions?.collectionWrite?.includes(collectionId) || 
  //                        permissions?.collectionRead?.includes(collectionId);
  
  // if (!isOwner && !hasSharedAccess) {
  //   throw new Error("You don't have permission to access this collection");
  // }
}

/**
 * Collections for one project, letting failures propagate. List callers must use
 * this over `getCollectionsForProject` below: a swallowed error returns [], which
 * is indistinguishable from "no collections" and paints an empty grid over a
 * broken query. Needs the (associatedProjectId ASC, updatedAt DESC) index on
 * `usersMyCollection`.
 */
export async function getCollectionsForProjectOrThrow(
  projectId: string,
  user_id: string,
  permissions: Permissions | null,
  teamScope: TeamScope | null = null
): Promise<CollectionInterface[]> {
  if (!userCollectionsDB || !projectId) return [];

  const q = query(
    userCollectionsDB,
    where("associatedProjectId", "==", projectId),
    // Outside the active team only the user's own collections are readable
    // (Personal is owner-only); needs (associatedProjectId, uid, updatedAt DESC).
    ...(teamScope ? teamScopeConstraints(teamScope) : [where("uid", "==", user_id)]),
    orderBy("updatedAt", "desc")
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => doc.data() as CollectionInterface);
}

export async function getCollectionsForProject(
  projectId: string,
  user_id: string,
  permissions: Permissions | null,
  teamScope: TeamScope | null = null
): Promise<CollectionInterface[]> {
  try {
    return await getCollectionsForProjectOrThrow(projectId, user_id, permissions, teamScope);
  } catch (error) {
    console.error('Error fetching collections for project:', error);
    return [];
  }
}

export async function getCollectionsForProjects(
  projectIds: string[], 
  user_id: string, 
  permissions: Permissions | null,
  teamScope: TeamScope | null = null
): Promise<Map<string, CollectionInterface[]>> {
  if (!userCollectionsDB || projectIds.length === 0) return new Map();
  
  try {
    const collectionsByProject = new Map<string, CollectionInterface[]>();
    
    // Initialize map with empty arrays
    projectIds.forEach(id => collectionsByProject.set(id, []));

    // Chunks of 30 (15 under a team scope), all in flight at once — see
    // getBoardsForProjects (#3924).
    const size = inChunkSize(teamScope);
    const batches: ReturnType<typeof getDocs>[] = [];
    for (let i = 0; i < projectIds.length; i += size) {
      const q = query(
        userCollectionsDB,
        where("associatedProjectId", "in", projectIds.slice(i, i + size)),
        ...(teamScope ? teamScopeConstraints(teamScope) : [where("uid", "==", user_id)]),
        orderBy("updatedAt", "desc")
      );
      batches.push(getDocs(q));
    }
    const snapshots = await Promise.all(batches);

    snapshots.forEach(snapshot => {
      snapshot.docs.forEach(doc => {
        const collection = doc.data() as CollectionInterface;
        if (collection.associatedProjectId) {
          const projectCollections = collectionsByProject.get(collection.associatedProjectId) || [];
          projectCollections.push(collection);
          collectionsByProject.set(collection.associatedProjectId, projectCollections);
        }
      });
    });

    return collectionsByProject;
  } catch (error) {
    console.error('Error fetching collections for projects:', error);
    return new Map();
  }
}

export async function getCollections(userID: string, collections: CollectionInterface[], limitRecords: number): Promise<CollectionInterface[]> {
  const queryArgs: any = [userCollectionsDB, where("uid", "==", userID), orderBy("updatedAt", "desc"), limit(limitRecords)]
  if (collections.length > 0) {
    queryArgs.splice(queryArgs.length - 1, 0, startAfter(collections[collections.length - 1].updatedAt))
  }
  const q = query.apply(this, queryArgs)
  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => doc.data() as CollectionInterface)
}

export async function updateOrSaveCollection(
  collection: CollectionInterface,
  newFileBlob: any = null,
  // A first save keeps the access fields the create stamped; later saves
  // leave them to the backend.
  opts: { isNew?: boolean } = {},
): Promise<CollectionInterface> {
  try {
    validateCollection(collection)
    
    let url: any = null;
    if (newFileBlob) { 
      url = await uploadImage(collection, newFileBlob)
    }
    
    const newCollection = produce(collection, (draft: CollectionInterface) => {
      if (newFileBlob) {
        draft.coverImage = url
      }
    })
    
    // Whole-doc merge save: the access fields are backend-owned, so they stay out.
    const saved: Record<string, any> = opts.isNew ? { ...newCollection } : stripAccessFields(newCollection);
    // Teams sharing: the backend writes a flagged collection's project pointer,
    // and files a new one aimed at a flagged project (it refuses a Personal one).
    const pointer = newCollection.associatedProjectId ?? null;
    const v2 =
      opts.isNew && pointer
        ? await (await import("./accessApi")).filesThroughBackend("collection", newCollection, pointer)
        : sharingV2Doc(newCollection);
    if (v2) {
      if (opts.isNew) saved.associatedProjectId = null;
      else delete saved.associatedProjectId;
    }
    const result = await updateRecord(collection.id, "usersMyCollection", saved);
    if (v2 && opts.isNew && pointer) {
      try {
        const { readers, editors } = await setItemProject(
          "collection",
          collection.id,
          pointer,
          newCollection.design_firm_id,
        );
        return { ...newCollection, readers, editors } as CollectionInterface;
      } catch (e) {
        console.warn("[updateOrSaveCollection] filing the new collection failed:", e);
        return { ...newCollection, associatedProjectId: null } as CollectionInterface;
      }
    }
    return newCollection as CollectionInterface;
  } catch (error) {
    console.log("error ", error); 
    throw error; // Re-throw to maintain error handling contract
  }
}

/**
 * Field-scoped write for a collection's product list.
 *
 * `updateOrSaveCollection` persists the WHOLE document, which is fine for a
 * dialog that reads and writes in one breath. It is not fine for a long-lived
 * editing session like the library's collection add mode: the products array
 * there is built from a snapshot taken when the session opened, so saving the
 * whole doc would write that snapshot's `name`, `coverImage`,
 * `associatedProjectId` etc. back over anything another surface (or another
 * tab) changed in the meantime.
 *
 * This touches only the two fields the add flow owns, plus `updatedAt`.
 * Callers are still responsible for basing `products`/`thumbnails` on a fresh
 * read — array fields are replaced wholesale, not merged.
 */
export async function updateCollectionProducts(
  collectionId: string,
  products: string[],
  thumbnails: CollectionThumbnail[],
): Promise<{ products: string[]; thumbnails: CollectionThumbnail[]; updatedAt: number }> {
  const updatedAt = Date.now()
  await updateFieldsInRecord(collectionId, "usersMyCollection", { products, thumbnails, updatedAt })
  return { products, thumbnails, updatedAt }
}


/** The fields the collection detail page may write, one field-scoped call each. */
export type CollectionEditableFields = Partial<
  Pick<
    CollectionInterface,
    | "name"
    | "description"
    | "coverImage"
    | "products"
    | "thumbnails"
    | "userMaterials"
    | "userImages"
    // Access-control flag, not content: written one-shot by the share modal
    // rather than through the detail page's debounced edit queue.
    | "is_public"
  >
>;

/**
 * Field-scoped write for any subset of a collection's editable fields — the
 * generalization of `updateCollectionProducts` above, with the same contract:
 * callers must base array fields on a fresh read (they are replaced
 * wholesale), and anything NOT passed is never touched, so a long-lived page
 * can't write a stale snapshot's `name` over another surface's rename.
 */
export async function updateCollectionFields(
  collectionId: string,
  fields: CollectionEditableFields,
): Promise<CollectionEditableFields & { updatedAt: number }> {
  const updatedAt = Date.now()
  await updateFieldsInRecord(collectionId, "usersMyCollection", { ...fields, updatedAt })
  return { ...fields, updatedAt }
}

/**
 * Upload a collection cover blob to GCS and return its public URL. Same
 * destination the collection modal's `uploadImage` uses; exported so the
 * detail page's hover-to-change cover shares it.
 */
export async function uploadCollectionCoverImage(
  uid: string,
  blob: Blob,
): Promise<string> {
  return (await uploadFileToGCloud(
    `userImages/${uid}`,
    `collection_cover_${generateID()}`,
    blob,
  )) as string
}

//a wrapper for async stuff
async function uploadImage(collection: CollectionInterface, blob: any) {
  return uploadCollectionCoverImage(collection.uid, blob)
}

export async function toggleProductInCollection(
  collection: CollectionInterface,
  productId: string, 
  productThumbnail: string,
  userImage: CollectionUserImages, 
  userMaterial: CollectionUserMaterials
): Promise<CollectionInterface> {
  // This function is used to add/remove items from a collection
  // It handles products, user images, and user materials - if the item exists, it removes it; if not, it adds it
  // Used throughout the app (like in CollectionPopover) to let users manage their collection contents
  
  // Create a new collection copy with immutable updates using Immer
  const newCollection: CollectionInterface | any = produce(collection, (draft: CollectionInterface) => {
    // Initialize products array if it doesn't exist
    if (!draft.products) draft.products = []

    // Handle product toggle
    if (productId) {
      // Check if product already exists in collection
      if (draft.products.includes(productId)) {         
        // Remove product from collection
        draft.products = draft.products.filter((p: string) => p != productId) 
        // Remove corresponding thumbnail
        const t = draft.thumbnails?.filter((t: any) => t.id != productId)
        draft.thumbnails = t == null ? [] : t
      }
      else { 
        // Add product to collection
        draft.products.push(productId) 
        // Add thumbnail for the product
        if (!draft.thumbnails) draft.thumbnails = []
        draft.thumbnails?.push({id: productId, src: productThumbnail})
      }
    }
    // Handle user image toggle
    else if (userImage) {
      // Initialize userImages array if it doesn't exist
      if (!draft.userImages) draft.userImages = []
      // Check if user image already exists in collection
      if (draft.userImages.find(u => userImage.id == u.id)) {              
        // Remove user image from collection
        draft.userImages = draft.userImages.filter((u: CollectionUserImages) => u.id != userImage.id)
        // Remove corresponding thumbnail
        const t = draft.thumbnails?.filter((t: any) => t.id != userImage.id)
        draft.thumbnails = t == null ? [] : t
      }
      else {          
        // Add user image to collection
        draft.userImages.push(userImage)
        // Add thumbnail for the user image
        if (!draft.thumbnails) draft.thumbnails = []
        draft.thumbnails?.push({id: userImage.id, src: userImage.src})
      }
    }
    // Handle user material toggle
    else if (userMaterial) {
      // Initialize userMaterials array if it doesn't exist
      if (!draft.userMaterials) draft.userMaterials = []
      // Check if user material already exists in collection
      if (draft.userMaterials.find(u => userMaterial.id == u.id)) {              
        // Remove user material from collection
        draft.userMaterials = draft.userMaterials.filter((u: CollectionUserMaterials) => u.id != userMaterial.id)
        // Remove corresponding thumbnail
        const t = draft.thumbnails?.filter((t: any) => t.id != userMaterial.id)
        draft.thumbnails = t == null ? [] : t
      }
      else {          
        // Add user material to collection
        draft.userMaterials.push(userMaterial)
        // Add thumbnail for the user material
        if (!draft.thumbnails) draft.thumbnails = []
        draft.thumbnails?.push({id: userMaterial.id, src: userMaterial.src})
      }
    }
  })
  
  // Save the updated collection to the database and return it
  return await updateOrSaveCollection(newCollection)
}